import {
  getUserAuthToken,
  getUserRefreshToken,
  setUserToken,
} from '@utils/userToken';

import { AuthTokenFragment } from './types';

const REFRESH_LOCK_NAME = 'cropink-session-refresh';

type SessionRefresherOptions = {
  requestToken: (refreshToken: string) => Promise<AuthTokenFragment>;
  onFailure: () => void;
};

/**
 * The API consumes a refresh token on first use (CROP-5219), so a second
 * refresh with the same token fails and logs the user out. Every 401 in the
 * tab shares the one refresh in flight, tabs take turns through a Web Lock,
 * and a refresh is skipped when another tab, or an earlier refresh here,
 * already replaced the token the request failed with.
 */
export const createSessionRefresher = ({
  requestToken,
  onFailure,
}: SessionRefresherOptions): ((failedAuthToken: string) => Promise<string>) => {
  let pending: Promise<string> | null = null;

  const getReplacedToken = (failedAuthToken: string): string | undefined => {
    const currentAuthToken = getUserAuthToken();

    return currentAuthToken && currentAuthToken !== failedAuthToken
      ? currentAuthToken
      : undefined;
  };

  const refresh = async (failedAuthToken: string): Promise<string> => {
    const replacedToken = getReplacedToken(failedAuthToken);
    if (replacedToken) return replacedToken;

    const refreshToken = getUserRefreshToken();
    if (!refreshToken) throw new Error('No refresh token to refresh with.');

    try {
      const sessionToken = await requestToken(refreshToken);
      if (!sessionToken.token)
        throw new Error('Session refresh returned no token.');

      setUserToken(sessionToken);

      return sessionToken.token;
    } catch (error) {
      // Without Web Locks another tab may have used the token first.
      const tokenFromOtherTab = getReplacedToken(failedAuthToken);
      if (tokenFromOtherTab) return tokenFromOtherTab;

      onFailure();

      throw error;
    }
  };

  const withTabLock = async (failedAuthToken: string): Promise<string> => {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return refresh(failedAuthToken);
    }

    return navigator.locks.request(REFRESH_LOCK_NAME, () =>
      refresh(failedAuthToken),
    );
  };

  return (failedAuthToken) => {
    pending ??= withTabLock(failedAuthToken).finally(() => {
      pending = null;
    });

    return pending;
  };
};
